import React from 'react';
import { type IEvent, type IAccountPool } from '../../reducers/eventsSlice';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { classNames } from '../../utilities/styleUtils';
import EventTimelineProgress from '../timeline/EventTimelineProgress';
import EventAttendeeAllocationGauge from '../timeline/EventAttendeeAllocationGauge';
import StatusPill, { type StatusColorVariant } from '../common/StatusPill';

interface EventItemProps {
  event: IEvent;
}

const isDecommissioned = (accountPool: IAccountPool | null): boolean => {
  if (!accountPool) return false;
  const decommissioningStatuses = ['pending-decommissioning', 'decommissioning', 'decommissioned'];
  return decommissioningStatuses.includes(accountPool.status.toLowerCase());
};

export default function EventItem({ event }: EventItemProps) {
  const accountPool = event.account_pool;
  const isEventDecommissioned = isDecommissioned(accountPool);
  const projectTail = event.project?.split('/').pop() ?? '';

  // Determine the status text based on approval and account pool status
  const getStatusText = () => {
    // Check approval status first
    switch (event.approval_status) {
      case 'PENDING':
        return 'For review';
      case 'REJECTED':
        return 'Rejected';
      case 'APPROVED':
        // For approved events, check account pool status
        if (isEventDecommissioned) {
          return 'Decomm...';
        }
        if (accountPool && accountPool.status.toLowerCase() === 'ready') {
          return 'Ready';
        }
        // If approved but account pool is not ready (pending or no accounts)
        return 'Pending';
      default:
        return 'For review';
    }
  };

  // Determine the color variant for the status pill
  const getStatusColorVariant = (): StatusColorVariant => {
    switch (event.approval_status) {
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'APPROVED':
        if (isEventDecommissioned) {
          return 'dark';
        }
        if (accountPool && accountPool.status.toLowerCase() === 'ready') {
          return 'success';
        }
        return 'neutral';
      default:
        return 'warning';
    }
  };

  // Determine the background and border colors based on the event status
  const getEventStyles = () => {
    // Check approval status first
    switch (event.approval_status) {
      case 'APPROVED':
        // Check if decommissioned for approved events
        if (isEventDecommissioned) {
          return 'bg-gray-100 ';
        }

        // Approved and ready (active): White background with dark green border
        if (accountPool && accountPool.status.toLowerCase() === 'ready') {
          return 'bg-white ';
        }
        // Approved and pending (upcoming): White background with green border
        return 'bg-gray-100 ';

      case 'PENDING':
        // Pending approval: Grey background with dark yellow border
        return 'bg-gray-100 ';

      case 'REJECTED':
        // Rejected: Grey background with red border
        return 'bg-gray-100 ';

      default:
        return 'bg-gray-100 ';
    }
  };
  // Determine the hover style based on the event status
  const getHoverStyle = () => {
    // Check approval status first
    switch (event.approval_status) {
      case 'APPROVED':
        // Check if decommissioned for approved events
        if (isEventDecommissioned) {
          return 'hover:bg-gray-200';
        }

        // For approved events (both ready and pending), use a light hover effect
        return 'hover:bg-gray-50';

      case 'PENDING':
      case 'REJECTED':
      default:
        // For all other events, use a slightly darker grey hover effect
        return 'hover:bg-gray-200';
    }
  };

  return (
    <div
      className={classNames(
        `relative grid grid-cols-12 gap-x-4 px-3 py-1 shadow-sm ring-1 ring-gray-900/5 sm:rounded-sm`,
        getEventStyles(),
        getHoverStyle(),
      )}
    >
      <div className="hidden sm:flex col-span-1 items-center">
        <StatusPill status={getStatusText()} colorVariant={getStatusColorVariant()} />
      </div>
      <div className="flex sm:hidden col-span-1 items-center">
        <StatusPill status={getStatusText()} colorVariant={getStatusColorVariant()} micro />
      </div>
      <div className="col-span-11 sm:col-span-5 md:col-span-3 lg:col-span-3 flex items-center justify-between text-sm">
        <div className="flex flex-col items-left justify-left overflow-hidden">
          <p className="text-sm font-semibold text-gray-900 truncate">{event.name}</p>
          <p className="text-xs text-gray-500 truncate">{event.slug}</p>
        </div>
      </div>
      <div className="hidden lg:flex lg:col-span-2 items-center justify-left text-sm">
        <span>{projectTail}</span>
      </div>
      <div className="hidden md:flex md:col-span-2 lg:col-span-2 items-center justify-left text-sm">
        {event.organization_account ? (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 truncate max-w-full"
            title={event.organization_account}
            data-testid="org-account-badge"
          >
            {event.organization_account}
          </span>
        ) : null}
      </div>
      <div className="hidden sm:flex sm:col-span-4 md:col-span-3 lg:col-span-2  items-center justify-center gap-x-4 text-sm">
        {/* event timeline */}
        <EventTimelineProgress event={event} />
      </div>
      <div className="hidden sm:flex sm:col-span-2 md:col-span-2 lg:col-span-1 items-center justify-center gap-x-4 text-sm">
        {/* event attendee allocations */}
        <EventAttendeeAllocationGauge event={event} />
      </div>
      <div className="hidden md:flex items-center justify-right justify-self-end text-sm">
        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
      </div>
    </div>
  );
}

export function EventItemHeader() {
  return (
    <div className="relative grid grid-cols-12 gap-x-4 px-2 py-2">
      <div className="hidden sm:flex col-span-1">Status</div>
      <div className="col-span-12 sm:col-span-5 md:col-span-3 lg:col-span-3 flex items-center justify-left text-sm">
        Event
      </div>
      <div className="hidden lg:flex lg:col-span-2 items-center justify-left text-xs">Lab Project</div>
      <div className="hidden md:flex md:col-span-2 lg:col-span-2 items-center justify-left text-sm">Org Account</div>
      <div className="hidden sm:flex sm:col-span-4 md:col-span-3 lg:col-span-2 items-center justify-center text-sm">
        Event Timeline
      </div>
      <div className="hidden sm:flex sm:col-span-2 md:col-span-2 lg:col-span-1 items-center justify-left text-sm">
        Allocations
      </div>
    </div>
  );
}
